import { useCallback } from 'react';

import { useNumberFormatter } from 'hooks/format';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export type ReviewCountVariant = 'fullText' | 'hidden' | 'numberOnly';
export type ReviewCount = number | string;

export const defaultReviewCountVariant: ReviewCountVariant = 'numberOnly';

export function useReviewCountFormatter() {
	const formatNumber = useNumberFormatter();
	return useCallback(
		(reviewCount: ReviewCount) =>
			is.number(reviewCount) ? formatNumber(reviewCount) : reviewCount,
		[formatNumber],
	);
}

export function useFullReviewCountTextGetter() {
	// TODO: General key with {{number}} + tPlural.
	const { t } = useI18n();
	const formatReviewCount = useReviewCountFormatter();
	return (reviewCount: ReviewCount) =>
		`${formatReviewCount(reviewCount)} ${
			reviewCount === 1 || reviewCount === '1'
				? t('product_details_review_rating_text')
				: t('product_details_review_ratings_text')
		}`;
}

export function useFullReviewCountText(reviewCount: ReviewCount) {
	return useFullReviewCountTextGetter()(reviewCount);
}

interface GetScreenReaderTextArgs {
	reviewCount?: ReviewCount;
	reviewCountVariant?: ReviewCountVariant;
	score: number;
}

export function useScreenReaderTextGetter() {
	const { tPlural } = useI18n();
	const formatNumber = useNumberFormatter();
	const getFullReviewCountText = useFullReviewCountTextGetter();
	return ({
		reviewCount = 0,
		reviewCountVariant = defaultReviewCountVariant,
		score,
	}: GetScreenReaderTextArgs) => {
		const starCountText = tPlural(
			'product_review_stars_screen_reader_text',
			score,
			{ starCount: formatNumber(score) },
		);
		return reviewCountVariant === 'hidden'
			? starCountText
			: `${starCountText}, ${getFullReviewCountText(reviewCount)}`;
	};
}

export function useScreenReaderText(args: GetScreenReaderTextArgs) {
	return useScreenReaderTextGetter()(args);
}
